"use client";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { ClientAddress } from "@/types/ClientAddress";

import { EmptyField } from "@/components/ui/empty-field";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Plus } from "lucide-react";
import { useState } from "react";
import AddressForm from "./address-form";

interface TabEnderecosProps {
  itens?: ClientAddress[];
}

export default function TabEnderecos({ itens }: TabEnderecosProps) {
  const { user } = useAuth();
  const cliCodigo = user?.cli_codigo ?? "";

  const [enderecos, setEnderecos] = useState<ClientAddress[]>(itens ?? []);
  const [adding, setAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleUpdate = (updated: ClientAddress) => {
    setEnderecos((prev) =>
      prev.map((e) => (e.cle_codigo === updated.cle_codigo ? updated : e))
    );
    setEditingIndex(null);
  };

  const handleAdd = (added: ClientAddress) => {
    setEnderecos((prev) => [...prev, added]);
    setAdding(false);
  };

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-n-primary">
          Meus endereços:
        </h2>
        {!adding && editingIndex === null && (
          <Button
            type="button"
            variant="border"
            size="sm"
            onClick={() => setAdding(true)}
          >
            Adicionar endereço <Plus size={14} />
          </Button>
        )}
      </div>

      <Separator />

      {adding && (
        <AddressForm
          cliCodigo={cliCodigo}
          onSaved={handleAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {enderecos.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">📍</div>
          <p className="text-gray-500 text-sm">Nenhum endereço cadastrado</p>
          <p className="text-gray-400 text-xs">
            Adicione seu primeiro endereço para começar
          </p>
        </div>
      ) : (
        <ul className="space-y-4">
          {enderecos.map((e, i) =>
            editingIndex === i ? (
              <li key={e.cle_codigo ?? i}>
                <AddressForm
                  cliCodigo={cliCodigo}
                  initial={e}
                  onSaved={handleUpdate}
                  onCancel={() => setEditingIndex(null)}
                />
              </li>
            ) : (
              <li key={e.cle_codigo ?? i} className="space-y-2">
                <span className="font-semibold text-n-text-primary text-sm">
                  {e.cle_nome || <EmptyField>Nome do endereço</EmptyField>}
                </span>
                <div className="flex gap-4 justify-between">
                  <div className="flex flex-col md:flex-row w-[85%] justify-between text-sm gap-4">
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Logradouro:
                      </span>
                      <span className="text-n-text-neutral">
                        {e.cle_endereco ? (
                          `${e.cle_endereco}${
                            e.cle_numero ? `, ${e.cle_numero}` : ""
                          }`
                        ) : (
                          <EmptyField>Endereço não informado</EmptyField>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Complemento:
                      </span>
                      <span className="text-n-text-neutral">
                        {e.cle_complemento || (
                          <EmptyField>Não informado</EmptyField>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Bairro:
                      </span>
                      <span className="text-n-text-neutral">
                        {e.cle_bairro || (
                          <EmptyField>Bairro não informado</EmptyField>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Cidade:
                      </span>
                      <span className="text-n-text-neutral">
                        {e.municipio || (
                          <EmptyField>Cidade não informada</EmptyField>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        CEP:
                      </span>
                      <span className="text-n-text-neutral">
                        {e.cle_cep || (
                          <EmptyField>CEP não informado</EmptyField>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-end items-start md:w-[15%] min-w-20">
                    <Button
                      type="button"
                      onClick={() => setEditingIndex(i)}
                      className="text-sm"
                      variant="link"
                      size="sm"
                    >
                      Editar
                      <ArrowRight size={14} />
                    </Button>
                  </div>
                </div>
                {i !== enderecos.length - 1 && <Separator />}
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
}
